import { Button, Modal, notification, Space, message as Message } from "antd";
import { DeleteOutlined, LinkOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { fetchWrapper } from "../../helpers";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { postsExtractor } from "../../helpers/posts-extractor";
import { useConfirm } from "../../hooks/useConfirm";
import threadsContentIcon from '../../pages/icons/threads-circle.svg';

const baseUrl = `${process.env.REACT_APP_API_URL}`;

const CreateSeriesOfPostsFromMessageModal = ({ message, snAction, socialNetworks, disabledButton = false }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [messages, setMessages] = useState([]);
	const [fbPageId, setFbPageId] = useState('');
	const [tokens, setTokens] = useState(undefined);
	const navigate = useNavigate();
	const [notificationApi, notificationContextHolder] =
		notification.useNotification();
	const { t } = useTranslation();
	const [isSNModalOpen, setIsSNModalOpen] = useState(false);

	const createPostFromMessage = async (postText) => {
		const newPostDescription = `${postText.trim()}`;
		const date = new Date();

		try {
			const result = await fetchWrapper
				.post(`${process.env.REACT_APP_API_URL}/assistant/summary`, {
					content: newPostDescription,
					socialNetwork: socialNetworks,
					date: date.toISOString(),
					isCompletedPost: true,
				});

			if (result) {
				return result;
			};
		} catch (err) {
			console.error(err);
			Message.error(t("functions.wrong"));
			return err;
		}
	};

	const createSeriesPostsFromMessage = async () => {
		const filteredMessages = messages.filter(item => item.trim() !== '');
		if (filteredMessages.length < 1) {
			return Message.error(t('assistant.notEnoughPosts'));
		}
		setIsLoading(true);
		Message.loading(t("functions.generating"));
		console.log(filteredMessages);

		for (let i = 0; i < filteredMessages.length; i++) {
			const result = await createPostFromMessage(filteredMessages[i]);
			if (result) {
				console.log(result);
				Message.success(t("assistant.createPostSuccess", { count: i + 1 }));
			} else {
				Message.error(t("assistant.createPostError", { count: i + 1 }));
			}
		}

		setIsLoading(false);
		setIsSNModalOpen(false);
		navigate('/posts');
	};

	const handleEditMessageFromSeries = (index, message) => {
		setMessages((prevState) => {
			return prevState.map((item, i) => {
				if (i === index) {
					return message;
				}
				return item;
			});
		});
	};

	const confirm = useConfirm();

	const handleRemoveMessageFromSeries = async (index, force = false) => {
		let isUserAgree = force;

		if (!force) {
			isUserAgree = await confirm({
				title: t("assistant.deleteSeriesElementTitle") || "Подтверждение",
				message: t("assistant.deleteSeriesElement") || "Вы уверены, что хотите удалить этот элемент серии?",
				confirmText: t("common.yes") || "Да",
				cancelText: t("common.no") || "Нет",
			});
		}

		if (isUserAgree) {
			setMessages(prevState => prevState.filter((item, i) => i !== index));
		}
	};

	const handleAddClearMessageForSeries = () => {
		setMessages((prevState) => {
			return [...prevState, ''];
		});
	};

	const openNotificationSocialNetwork = ({ message, description }) => {
		const key = `open${Date.now()}`;
		const btn = (
			<Space>
				<Button
					type="primary"
					size="small"
					onClick={() => navigate("/profile")}
				>
					{t("posts.goToProfile")}
				</Button>
			</Space>
		);
		notificationApi.open({
			message: message || t("functions.error"),
			description: description || t("posts.unknownError"),
			btn,
			key,
		});
	};
	const handleChangeSocialNetworks = (value) => {
		if (!tokens[value.toLowerCase()]) {
			return openNotificationSocialNetwork({
				message: t("functions.error"),
				description: t(`posts.notLinked-${value.toLowerCase()}`),
			});
		}
		snAction((prevState) => {
			if (prevState.includes(value)) {
				return prevState.split(',').filter(item => item !== value).join(',')
			} else {
				if (prevState === '') {
					return value
				} else {
					return `${prevState},${value}`
				}
			}
		});
	}

	useEffect(() => {
		if (isSNModalOpen && !fbPageId && !tokens) {
			fetchWrapper
				.get(`${baseUrl}/auth/getSocialMediaTokens/`)
				.then((res) => {
					let filteredSn = [];
					const filteredToken = {
						facebook: false,
						instagram: false,
						linkedin: false,
                        threads: false,
					};
					res.forEach((token) => {
						if (token.socialMediaType === "FACEBOOK") {
							filteredToken.facebook = true;
							filteredSn.push('Facebook')
						}
						if (token.socialMediaType === "INSTAGRAM") {
							filteredToken.instagram = true;
							filteredSn.push('Instagram')
						}
						if (token.socialMediaType === "LINKEDIN") {
							filteredToken.linkedin = true;
							filteredSn.push('LinkedIn')
						}
                        if (token.socialMediaType === "THREADS") {
                            filteredToken.threads = true;
                            filteredSn.push('Threads')
                        }
					});
					setTokens(prevState => filteredToken);
					snAction(filteredSn.join(','));
				})
				.catch(() => { });
			fetchWrapper.get(`${baseUrl}/profile/`).then((res) => {
				setFbPageId(res.facebookPageId);
			});
		}
	}, [isSNModalOpen]);

	useEffect(() => {
		const newMessages = postsExtractor(message.content);

		if (newMessages.length) {
			setMessages(newMessages);
		} else {
			const fallbackMessages = message.content.split('\n').filter((msg) => msg.trim().length > 10);
			setMessages(fallbackMessages);
		}
	}, [isSNModalOpen]);

	return (
		<>
			{notificationContextHolder}
			<button
				disabled={disabledButton}
				className='shadow-md flex items-center justify-center bg-white text-primary hover:bg-violet-500 hover:text-white hover:shadow-lg aspect-square p-1 rounded-full text-md border w-10 h-10 duration-500'
				onClick={() => setIsSNModalOpen(true)}
			>
				<LinkOutlined />
			</button>
			<Modal
				confirmLoading={isLoading}
				title={t("assistant.createsSeriesPostsFromThisMessageBtn")}
				open={isSNModalOpen}
				onOk={createSeriesPostsFromMessage}
				onCancel={() => setIsSNModalOpen(false)}
				okText={t('posts.create')}
				cancelText={t('posts.cancel')}
			>
				<div
					className="text-center flex flex-col gap-4 justify-center py-4 border-b border-solid border-b-[color:var(--Primary-200,#E9D7FE)]"
				>
					<div className="pb-4 text-center flex gap-4 justify-center border-b border-solid border-b-[color:var(--Primary-200,#E9D7FE)]">
						<pre className='text-left !text-xs text-pretty text-wrap font-inherit '>
							{t('assistant.seriesPostsInstruction')}
						</pre>
					</div>

					<div className="text-center flex gap-4 justify-center py-4 border-b border-solid border-b-[color:var(--Primary-200,#E9D7FE)]">
						<div style={{ opacity: socialNetworks?.includes("LinkedIn") ? 1 : 0.4 }}
							onClick={() => handleChangeSocialNetworks("LinkedIn")}
							className="flex flex-col flex-1 cursor-pointer">
							<img
								loading="lazy"
								src="https://cdn.builder.io/api/v1/image/assets/TEMP/d25408c5dff59b86cb5e75674b2a798a87cd455bde0adf0ceb8c9a670d1405e8?apiKey=300ff06bbf3a4f5c9c73ca7577720bf1&"
								className="self-center w-6 aspect-square"
							/>
							<div className="mt-1">LinkedIn</div>
						</div>

						{process.env.REACT_APP_IS_HIDE_FACEBOOK !== 'true' &&
							<div style={{ opacity: socialNetworks?.includes("Facebook") ? 1 : 0.4 }}
								onClick={() => handleChangeSocialNetworks("Facebook")}
								className={`flex flex-col flex-1 cursor-pointer ${process.env.REACT_APP_IS_HIDE_FACEBOOK === 'true' && '!hidden'}`}>
								<img
									loading="lazy"
									src="https://cdn.builder.io/api/v1/image/assets/TEMP/f8432315b7e3752ab28ffd9571f771342eef6b676c1f20e4b8a5e6ee48c70349?apiKey=300ff06bbf3a4f5c9c73ca7577720bf1&"
									className="self-center w-6 aspect-square"
								/>
								<div className="mt-1">Facebook</div>
							</div>
						}
						<div style={{ opacity: socialNetworks?.includes("Instagram") ? 1 : 0.4 }}
							onClick={() => handleChangeSocialNetworks("Instagram")}
							className="flex flex-col flex-1 cursor-pointer">
							<img
								loading="lazy"
								src="https://cdn.builder.io/api/v1/image/assets/TEMP/497038a4df4615b7ef35db19c49364c0fdcd7e3749f9681fe807b55de2426e88?apiKey=300ff06bbf3a4f5c9c73ca7577720bf1&"
								className="self-center w-6 aspect-square"
							/>
							<div className="mt-1">Instagram</div>
						</div>
                        <div style={{ opacity: socialNetworks?.includes("Threads") ? 1 : 0.4 }}
                             onClick={() => handleChangeSocialNetworks("Threads")}
                             className="flex flex-col flex-1 cursor-pointer">
                            <img
                                loading="lazy"
                                src={threadsContentIcon}
                                className="self-center w-6 h-6"
                            />
                            <div className="mt-1">Threads</div>
                        </div>
					</div>

					<div className='flex flex-col gap-5'>
						{messages.map((message, index) => (
							<div
								className='relative'
								key={index}
							>
								<textarea
									placeholder='Введите текст поста'
									className='w-full resize-none border rounded-md p-3'
									rows="4"
									value={message}
									onChange={e => handleEditMessageFromSeries(index, e.target.value)}
									onBlur={() => {
										if (message.trim() === '') handleRemoveMessageFromSeries(index, true);
									}}
								/>
								<button
									className='absolute -top-3 -right-3 z-10 text-white bg-red-400 border w-7 h-7 rounded-full shadow-xl hover:bg-red-600'
									onClick={() => handleRemoveMessageFromSeries(index, message.trim() === '')}
								>
									<DeleteOutlined />
								</button>
							</div>
						))}
						<button
							className='logout-button !mt-0'
							onClick={handleAddClearMessageForSeries}
						>
							{t('assistant.addNewPostToSeries')}
						</button>
					</div>

				</div>
			</Modal>
		</>
	);
};

export default CreateSeriesOfPostsFromMessageModal;